import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import {
  AvailableReferenceData,
  ReferenceContact,
  ReferenceRequestPayload,
  ReferenceToSubmit,
} from '../types/referenceTypes';
import { EmailTemplate } from 'components/CandidatesList/MarketplaceProspectQuickView/types';
import { referenceQueries } from 'queryKeys/references';
import { Reference as ReferenceResponse, ReferenceCheck, ReferenceRequest } from 'types';
import EmailTemplatesAPI from 'api/emailTemplatesAPI';
import referenceCheckAPI from 'api/referenceCheckAPI';
import referenceCheckV2API from 'api/referenceCheckV2API';
import ReferenceRequestsAPI from 'api/referenceRequests';
import ReferencesAPI from 'api/referencesAPI';
import referencesAPI from 'api/referencesAPI';
import referenceTemplateListAPI from 'api/referenceTemplateListAPI';
import usersAPI from 'api/usersAPI';

export const createReferenceCheck = (
  payload: ReferenceToSubmit | FormData
): Promise<ReferenceCheck> => {
  return referenceCheckV2API.create(payload).then((res) => res);
};

export const createReferenceContact = (payload: ReferenceToSubmit): Promise<ReferenceResponse> =>
  ReferencesAPI.createReferenceContact(payload).then((res) => res);

export const deleteReference = (referenceId: number): Promise<''> => {
  return referenceCheckAPI.destroy(referenceId).then((res) => res);
};

export const editReference = (
  referenceId: number,
  payload: ReferenceToSubmit | FormData
): Promise<ReferenceCheck> => {
  return referenceCheckV2API.edit(referenceId, payload).then((res) => res);
};

export const requestReference = (candidateId: number, payload: ReferenceRequestPayload) =>
  ReferenceRequestsAPI.postRequest(candidateId, payload).then((res) => res);

export const useQueryGetReferenceTemplates = (
  districtId: number,
  options?: Omit<UseQueryOptions<AvailableReferenceData[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<AvailableReferenceData[], AxiosError> => {
  return useQuery({
    queryKey: referenceQueries.references.templates(districtId).queryKey,
    queryFn: () => referenceTemplateListAPI.fetchReferences().then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryGetReferenceContacts = (
  candidateId: number
): UseQueryResult<ReferenceContact[], AxiosError> => {
  return useQuery({
    queryKey: referenceQueries.references.contacts(candidateId).queryKey,
    queryFn: () => referencesAPI.getReferences(candidateId).then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const getEmailTemplatesForReferences = () =>
  EmailTemplatesAPI.getTemplatesForReferences().then((res) => res);

export const useQueryGetEmailTemplatesForReferences = (
  candidateId: number,
  options?: Omit<UseQueryOptions<EmailTemplate[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<EmailTemplate[], AxiosError> => {
  return useQuery({
    queryKey: referenceQueries.references.emailTemplates(candidateId).queryKey,
    queryFn: () => getEmailTemplatesForReferences().then((res) => res),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryGetCompletedAndDraftReferences = (
  candidateId: number,
  options?: Omit<UseQueryOptions<ReferenceCheck[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ReferenceCheck[], AxiosError> => {
  return useQuery({
    queryKey: referenceQueries.references.completedAndDraft(candidateId).queryKey,
    queryFn: () =>
      usersAPI.fetchReferenceChecks(candidateId).then((res) => {
        return res;
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useQueryPendingReferences = (
  candidateId: number,
  options?: Omit<UseQueryOptions<ReferenceRequest[], AxiosError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ReferenceRequest[], AxiosError> => {
  return useQuery({
    queryKey: referenceQueries.references.pending(candidateId).queryKey,
    queryFn: () =>
      ReferenceRequestsAPI.getRequests(candidateId).then((res) => {
        return res;
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};
